import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../services/apiClient';
import { linkForItem } from '../utils/deepLink';

// Current Activity — what each PM, office person and Executive has been working on
// lately (Pat, Sep 2026). A pill beside Request Time Off on the office dashboards; it
// opens a panel of people, each with the last few things they touched, and every line
// opens the thing itself — the job, the card, the PO.

const ago = iso => {
  if (!iso) return 'nothing lately';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 2) return 'just now'; if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  const d = Math.floor(m / 1440); return d === 1 ? 'yesterday' : `${d} days ago`;
};
const ROLE_TINT = { Executive: '#7c3aed', PM: '#1d4ed8', Office: '#0f766e', OfficeStaff: '#0f766e', Supervisor: '#b45309', Worker: '#92400e', Shop: '#4d7c0f' };
// The Field button covers supervisors and workers together (Pat, Sep 27 2026).
const WHO_MATCH = { field: p => p.role === 'Supervisor' || p.role === 'Worker' };
const sinceLabel = iso => (iso ? `last seen ${ago(iso)}` : 'never seen in the app');

// mine = the person's own recent work (the PM and office dashboards); the company-wide
// view lives on the Executive page.
export function CurrentActivityPill({ style, mine = false }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 999, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#3730a3', fontSize: 13, fontWeight: 700, cursor: 'pointer', ...style }}>
        <span>👀</span> {mine ? 'My Activity' : 'Current Activity'}
      </button>
      {open && <CurrentActivityPanel onClose={() => setOpen(false)} />}
    </>
  );
}

export default function CurrentActivityPanel({ onClose, inline = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [days, setDays] = useState(7);
  const [who, setWho] = useState('');       // '' = everyone, else a role

  useEffect(() => {
    setData(null);
    apiFetch(`/whos-working-on?days=${days}&per=100`).then(setData).catch(e => setErr(e?.error || 'Could not load activity.'));
  }, [days]);

  const everyone = !!data?.everyone;
  const people = (data?.people || []).filter(p => !everyone || !who || (WHO_MATCH[who] ? WHO_MATCH[who](p) : p.role === who));
  const quiet = everyone ? (data?.quiet || []).filter(p => !who || (WHO_MATCH[who] ? WHO_MATCH[who](p) : p.role === who)) : [];
  const [quietOpen, setQuietOpen] = useState(false);
  const [openPerson, setOpenPerson] = useState(null);   // a person's card opened all the way up (Ryan, Oct 3 2026)
  const go = it => {
    const target = linkForItem({ scope: it.scope, projectId: it.project_id, data: it.meta });
    if (target) { onClose && onClose(); navigate(target); }
  };
  const label = it => {
    // Number and name; a name that already starts with the number says both (it showed "2628" alone).
    const job = it.job_number ? (it.job_name ? (String(it.job_name).startsWith(String(it.job_number)) ? it.job_name : `${it.job_number} ${it.job_name}`) : it.job_number) : it.job_name || '';
    const NAMES = { exec: 'Executive page', schedule: 'Schedule', equipment: 'Equipment', time: 'Time clock', po: 'Purchase order', extra_work: 'Extra work', stock: 'Stock', vertical_drilling: 'Vertical drilling', horizontal_drilling: 'Directional drilling', loops: 'Loops', small_jobs: 'Small job', submittals: 'Submittal', nve: 'NVE', usa_digs: 'USA dig', shop: 'Shop' };
    return job || NAMES[it.scope] || String(it.scope || '').replace(/_/g, ' ');
  };

  const itemRow = it => {
    const target = linkForItem({ scope: it.scope, projectId: it.project_id, data: it.meta });
    return (
      <button key={it.key} onClick={() => go(it)} disabled={!target}
        style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderTop: '1px solid #f3f4f6', padding: '7px 2px', cursor: target ? 'pointer' : 'default', font: 'inherit' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: target ? '#1d4ed8' : '#374151', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label(it)}{it.count > 1 ? <span style={{ fontWeight: 500, color: '#9ca3af' }}> · {it.count}×</span> : null}</span>
          <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>{ago(it.at)}</span>
        </div>
        {/* The whole line, and for an update or note the whole comment — no going to the job to
            read the rest (Pat, Oct 6 2026). */}
        {it.full_text ? (<>
          <div style={{ fontSize: 12, color: '#6b7280' }}>{String(it.action || '').replace(/:\s*".*$/, ':')}</div>
          <div style={{ fontSize: 12.5, color: '#1f2937', whiteSpace: 'pre-wrap', lineHeight: 1.45, marginTop: 3, padding: '6px 9px', background: '#f9fafb', borderLeft: '3px solid #e5e7eb', borderRadius: 4 }}>{it.full_text}</div>
        </>) : (
          <div style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'normal', wordBreak: 'break-word' }}>{it.action}</div>
        )}
      </button>
    );
  };
  const personHead = (p, big = false) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <div style={{ width: big ? 40 : 32, height: big ? 40 : 32, borderRadius: '50%', background: `${ROLE_TINT[p.role] || '#6b7280'}18`, color: ROLE_TINT[p.role] || '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: big ? 14 : 12, flexShrink: 0 }}>
        {p.name.split(' ').map(x => x[0]).slice(0, 2).join('')}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: big ? 17 : 14, fontWeight: 800, color: '#111827' }}>{p.name} <span style={{ fontSize: 11, fontWeight: 700, color: ROLE_TINT[p.role] || '#6b7280' }}>{p.role}</span></div>
        <div style={{ fontSize: 11.5, color: '#6b7280' }}>{p.actions ? `${p.actions} action${p.actions === 1 ? '' : 's'} · last ${ago(p.last_at)}` : `nothing in the last ${days === 1 ? 'day' : days + ' days'}`}{!p.actions && everyone ? ` · ${sinceLabel(p.seen_at)}` : ''}</div>
      </div>
    </div>
  );

  const shell = inline
    ? { outer: { }, inner: { background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column' } }
    : { outer: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' },
        inner: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 820, maxHeight: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' } };
  return (
    <div onClick={inline ? undefined : onClose} style={shell.outer}>
      <div onClick={e => e.stopPropagation()} style={shell.inner}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#111827' }}>👀 {data && !everyone ? 'My Activity' : 'Current Activity'}</div>
          {everyone && <div style={{ display: 'inline-flex', border: '1px solid #e5e7eb', borderRadius: 9, overflow: 'hidden' }}>
            {[['', 'Everyone'], ['Executive', 'Executives'], ['PM', 'PMs'], ['Office', 'Office'], ['field', 'Field'], ['Shop', 'Shop']].map(([k, l]) => (
              <button key={k} onClick={() => setWho(k)} style={{ padding: '5px 11px', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer', background: who === k ? '#111827' : '#fff', color: who === k ? '#fff' : '#374151' }}>{l}</button>
            ))}
          </div>}
          <select value={days} onChange={e => setDays(Number(e.target.value))} style={{ fontSize: 12.5, padding: '5px 8px', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            <option value={1}>Today</option><option value={3}>Last 3 days</option><option value={7}>Last 7 days</option><option value={14}>Last 2 weeks</option><option value={30}>Last 30 days</option>
          </select>
          <span style={{ flex: 1 }} />
          {!inline && <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>×</button>}
        </div>
        <div style={{ overflowY: 'auto', padding: '14px 20px 20px' }}>
          {err && <div style={{ color: '#dc2626', fontSize: 13 }}>{err}</div>}
          {!data && !err && <div style={{ color: '#9ca3af', fontSize: 13, padding: 20 }}>Loading…</div>}
          {data && quiet.length > 0 && (
            // Who has gone quiet: no action and no sign-in for a week, whatever range is showing.
            <div style={{ border: '1px solid #fecaca', background: '#fff1f2', borderRadius: 12, padding: '10px 14px', marginBottom: 14 }}>
              <button onClick={() => setQuietOpen(o => !o)} style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#9f1239' }}>😴 Not active in a week · {quiet.length}</span>
                <span style={{ fontSize: 12, color: '#be123c', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {quietOpen ? 'hide' : quiet.slice(0, 6).map(p => p.name).join(', ') + (quiet.length > 6 ? '…' : '')}
                </span>
                <span style={{ color: '#be123c', fontSize: 12 }}>{quietOpen ? '▲' : '▼'}</span>
              </button>
              {quietOpen && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6, marginTop: 10 }}>
                  {quiet.map(p => (
                    <div key={p.id} style={{ background: '#fff', border: '1px solid #fecdd3', borderRadius: 9, padding: '7px 10px', display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: '#111827', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name} <span style={{ fontSize: 11, fontWeight: 700, color: ROLE_TINT[p.role] || '#6b7280' }}>{p.role}</span></span>
                      <span style={{ fontSize: 11, color: '#9f1239', whiteSpace: 'nowrap' }}>{sinceLabel(p.seen_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {data && people.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13, padding: 20 }}>Nobody to show.</div>}
          <div style={{ display: 'grid', gridTemplateColumns: everyone ? 'repeat(auto-fill, minmax(360px, 1fr))' : '1fr', gap: 12 }}>
            {people.map(p => (
              <div key={p.id} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 14px', opacity: p.actions ? 1 : 0.6, display: 'flex', flexDirection: 'column' }}>
                {/* The person is the button: tap to open their whole list full screen (Ryan, Oct 3 2026). */}
                <button onClick={() => setOpenPerson(p)} title="Open all the way up"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', textAlign: 'left' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>{personHead(p)}</div>
                  <span style={{ fontSize: 16, color: '#9ca3af', flexShrink: 0 }} aria-hidden>⤢</span>
                </button>
                {/* The list scrolls inside the card — the scrollbar stays on the side. */}
                <div style={{ maxHeight: 320, overflowY: 'auto', scrollbarGutter: 'stable', paddingRight: 4 }}>
                  {p.items.map(itemRow)}
                </div>
                {p.items.length > 5 && <button onClick={() => setOpenPerson(p)} style={{ marginTop: 6, alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, color: '#1d4ed8', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Open all the way up ›</button>}
              </div>
            ))}
          </div>
        </div>
      </div>
      {openPerson && (
        <div onClick={() => setOpenPerson(null)} style={{ position: 'fixed', inset: 0, zIndex: 6200, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'stretch', justifyContent: 'center', padding: 'max(env(safe-area-inset-top), 14px) 14px max(env(safe-area-inset-bottom), 14px)' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 900, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 16px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>{personHead(openPerson, true)}</div>
              <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{days === 1 ? 'today' : `last ${days} days`}</span>
              <button onClick={() => setOpenPerson(null)} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 26, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ overflowY: 'auto', padding: '6px 18px 18px', flex: 1 }}>
              {openPerson.items.length === 0 ? <div style={{ color: '#9ca3af', fontSize: 13, padding: 20 }}>Nothing in this range.</div> : openPerson.items.map(itemRow)}
            </div>
            <div style={{ borderTop: '1px solid #e5e7eb', padding: '10px 14px' }}>
              <button onClick={() => setOpenPerson(null)} style={{ width: '100%', minHeight: 44, borderRadius: 10, border: '1.5px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
